import axios from "axios";
import {Todo} from "@/api/todo";
import {Flame} from "@/api/flame";

export interface Attachment {
  id: string | null;
  name: string | null;
  sizeByte: number | null;
  mimeType: string | null;
  uploadedAtUtc: Date;
  projectRef?: string;
}

export enum UploadStatus {
  EXIST = "EXIST",
  EMPTY = "EMPTY",
  STORAGE_FAILED = "STORAGE_FAILED",
  FAILED = "FAILED",
  OK = "OK",
}


export interface Project {
  id: string | null;
  title: string;
  description: string;
  currentStep: string;
  ownerRef: string | null;
  blackBoardRef: string;
  createdUtc: Date;
  updatedUtc?: Date | null;
  reminderAtUtc: Date | null;
  deletedUtc?: Date | null;
  archivedUtc?: Date | null;
  start: Date;
  finished?: Date | null;
  proxyOwner?: string | null;
  proxyOwnerFrom?: Date | null;
  proxyOwnerUntil?: Date | null;
}

export interface ProjectSearchRequest {
  searchTerm?: string;
  page: number;
  pageSize: number;
  sortBy: string;
  ownerRef?: string;
  sortDesc: boolean;
  reminderProject?: boolean;
}

export interface ProjectSearchResult {
  totalPages: number;
  totalElements: number;
  page: number;
  pageSize: number;
  projects: Project[];
}

export interface RevertArchivedProjectRequest {
  projectId: string;
  blackBoardRef: string;
  step: string;
  ownerRef: string;
}

interface ProjectApi {
  listByTaskBoard(taskboardId: string ): Promise<Project[]>;
  getById(id: string): Promise<Project | null>;
  add(project: Project): Promise<Project>;
  edit(id: string | null, project: Project): Promise<Project>;
  delete(id: string): Promise<void>;

  archive(id: string): Promise<void>;
  mapDatesForProject(project: Project): Project;
  move(
      projectId: string,
      taskboardId: string,
      step: string,
      assigneeRef: string | null
  ): Promise<void>;

  listAttachments(projectRef: string): Promise<Attachment[]>;
  countAttachments(projectRef: string): Promise<number>;
  addAttachment(projectRef: string, file: File): Promise<UploadStatus>;
  deleteAttachment(projectRef: string, id: string): Promise<void>;
  generateDownloadLink(projectRef: string, id: string): string;
  renameAttachment(projectRef: string, id: string, name: string): Promise<void>;
  listProjects(projectSearchRequest: ProjectSearchRequest): Promise<ProjectSearchResult>
  revertArchivedProject(revertArchivedProjectRequest: RevertArchivedProjectRequest): Promise<void>
}

export const projectApi: ProjectApi = {
  async listByTaskBoard(taskboardId) {
    const uri =`/api/projects/blackboard/${taskboardId}`;
    const result = (await axios.get(uri)).data;
    if (result) {
      return result.map((v: Project) => this.mapDatesForProject(v));
    }
    return [];
  },
  async getById(projectId) {
    const result = (await axios.get(`/api/projects/${projectId}`)).data;
    return result ? this.mapDatesForProject(result) : result;
  },
  async edit(id, project) {
    const result = (await axios.put(`/api/projects/${id}`, project)).data;
    return result ? this.mapDatesForProject(result) : result;

  },
  async add(project) {
    const result = (await axios.post("/api/projects", project)).data;
    return result ? this.mapDatesForProject(result) : result;
  },
  async delete(id) {
    return await axios.delete(`/api/projects/${id}`);
  },
  async archive(id) {
    await axios.put(`/api/projects/${id}/_archive`);
  },
  async move(id, taskboardId, step, assigneeRef) {
    return await axios.put(`/api/projects/${id}/_move`, {
      taskboardRef: taskboardId,
      step: step,
      assigneeRef: assigneeRef,
    });
  },
  mapDatesForProject(project: Project): Project {
    const mappedProject =  {
      ...project,
      createdUtc: project.createdUtc ? new Date(project.createdUtc) : null,
      updatedUtc: project.updatedUtc ? new Date(project.updatedUtc) : null,
      deletedUtc: project.deletedUtc ? new Date(project.deletedUtc) : null,
      archivedUtc: project.archivedUtc ? new Date(project.archivedUtc) : null,
      finished: project.finished ? new Date(project.finished) : null,
      start: project.start ? new Date(project.start) : null,
      proxyOwnerFrom: project.proxyOwnerFrom ? new Date(project.proxyOwnerFrom) : null,
      proxyOwnerUntil: project.proxyOwnerUntil ? new Date(project.proxyOwnerUntil) : null,
      reminderAtUtc: project.reminderAtUtc
          ? new Date(project.reminderAtUtc)
          : null
    } as Project;

    mappedProject.start?.setSeconds(0);
    mappedProject.start?.setMilliseconds(0);
    mappedProject.finished?.setSeconds(0);
    mappedProject.finished?.setMilliseconds(0);
    mappedProject.proxyOwnerFrom?.setSeconds(0);
    mappedProject.proxyOwnerFrom?.setMilliseconds(0);
    mappedProject.proxyOwnerUntil?.setSeconds(0);
    mappedProject.proxyOwnerUntil?.setMilliseconds(0);

    return mappedProject;
  },

  async countAttachments(projectRef) {
    return (await axios.get(`/api/projects/${projectRef}/attachments/_count`))
        .data;
  },
  async listAttachments(projectRef) {
    const result = (await axios.get(`/api/projects/${projectRef}/attachments`))
        .data as Attachment[];
    if (result) {
      return result.map(
          (v) =>
              ({
                ...v,
                uploadedAtUtc: v.uploadedAtUtc ? new Date(v.uploadedAtUtc) : null,
              } as Attachment)
      );
    }
    return [];
  },

  async addAttachment(projectRef, file) {
    const formData = new FormData();
    formData.append("file", file);
    return (await axios.post(`/api/projects/${projectRef}/attachments`, formData)).data;
  },

  async deleteAttachment(projectRef, id) {
    return await axios.delete(`/api/projects/${projectRef}/attachments/${id}`);
  },

  generateDownloadLink(projectRef: string, id: string): string {
    return `/api/projects/${projectRef}/attachments/${id}`;
  },
  async listProjects(searchRequest) {
    searchRequest.reminderProject = true;
    const result = (await axios.post(`/api/projects/_search`, searchRequest))
        .data;
    if (result.projects) {
      result.projects = result.projects.map((v: Project) => this.mapDatesForProject(v));
    }
    return result
  },

  async renameAttachment(projectRef, id, name) {
    await axios.put(
        `/api/projects/${projectRef}/attachments/${id}/rename`,
        `{ "newDocumentName": "${name}" }`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
    );
  },
  async revertArchivedProject(revertArchivedProjectRequest) {
    return await axios.put(`/api/projects/${revertArchivedProjectRequest.projectId}/revertArchivedProject`, revertArchivedProjectRequest);
  },
};
